import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { authAPI } from '../../utils/api';
interface OTPVerificationProps {
  phone: string;
  email?: string;
  method?: 'whatsapp' | 'email';
  onComplete: (code: string) => void;
  onResend: () => void;
  onSwitchMethod?: (newMethod: 'whatsapp' | 'email') => void;
}
export function OTPVerification({
  phone,
  email,
  method = 'whatsapp',
  onComplete,
  onResend,
  onSwitchMethod
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const handleChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit if complete
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerify(newOtp.join(''));
    }
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      // Focus last input
      inputRefs.current[5]?.focus();
      // Auto-submit
      handleVerify(pastedData);
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleVerify = async (code: string) => {
    setIsLoading(true);
    try {
      await onComplete(code);
    } catch (error) {
      console.error(error);
      // Reset OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };
  const handleResend = async () => {
    if (timer === 0) {
      await onResend();
      setTimer(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };
  const handleSwitchMethod = async () => {
    if (onSwitchMethod) {
      const newMethod = method === 'whatsapp' ? 'email' : 'whatsapp';
      await onSwitchMethod(newMethod);
      setTimer(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };
  // Determine what to display based on method
  const displayTarget = method === 'email' ? email : phone;
  const displayText = method === 'email' ? 'تحقق من بريدك الإلكتروني' : 'تحقق من رقم هاتفك';
  return <div className="space-y-6 text-center" dir="rtl" data-id="element-74">
      <div data-id="element-75">
        <h3 className="text-lg font-semibold text-gray-900" data-id="element-76">{displayText}</h3>
        <p className="text-sm text-gray-500 mt-1" data-id="element-77">
          أرسلنا رمز التحقق إلى{' '}
          <span className="font-medium text-gray-700" data-id="element-78">{displayTarget}</span>
        </p>
      </div>

      <div className="flex justify-center gap-2 flex-row-reverse" onPaste={handlePaste} data-id="element-79">
        {otp.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:border-[#4f1964] focus:ring-2 focus:ring-purple-200 outline-none transition-all" disabled={isLoading} data-id="element-80" />)}
      </div>

      <p className="text-xs text-gray-400" data-id="element-81">
        {method === 'email' ? '💡 يمكنك لصق الرمز مباشرة' : '💡 يمكنك لصق الرمز مباشرة'}
      </p>

      <div className="flex flex-col items-center gap-4" data-id="element-82">
        <Button onClick={() => handleVerify(otp.join(''))} isLoading={isLoading} disabled={otp.some(d => !d)} fullWidth data-id="element-83">
          تحقق
        </Button>

        <div className="flex flex-col items-center gap-2" data-id="element-84">
          <button onClick={handleResend} disabled={timer > 0} className={`text-sm font-medium ${timer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-[#4f1964] hover:text-[#3d1350]'}`} data-id="element-85">
            {timer > 0 ? `إعادة الإرسال خلال ${timer} ثانية` : 'إعادة إرسال الرمز'}
          </button>

          {onSwitchMethod && <button onClick={handleSwitchMethod} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors" data-id="element-86">
              {method === 'whatsapp' ? <>
                  <Mail className="w-3 h-3" data-id="element-87" />
                  <span data-id="element-88">إرسال عبر الإيميل بدلاً من ذلك</span>
                </> : <>
                  <MessageSquare className="w-3 h-3" data-id="element-89" />
                  <span data-id="element-90">إرسال عبر واتساب بدلاً من ذلك</span>
                </>}
            </button>}
        </div>
      </div>
    </div>;
}